import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RESCUE_POPUP_SOURCE } from '@components/popups/RescuePopup/RescuePopup.constants';
import {
  LoginEventPopupFlow,
  MODAL_SEARCH_PARAM_VALUE,
  SearchParam,
} from '@constants/common';
import { PopupDataMap, PopupName, usePopups } from '@hooks/popups/usePopups';
import { useLoginThen } from '@hooks/useLoginThen';
import { useDeleteSearchParams } from '@hooks/useDeleteSearchParams';
import { isIslandMounted } from '@island-runtime/hosts';
import { IslandName } from '@island-runtime/types';
import { UserDataModel } from '@models/userData';

// Every popup a link (an email, a marketing link) opens, in one place: the SPA
// and the islands read the same host URL.

type AutoOpenParam = {
  param: SearchParam;
  /** Without it, any value opens the popup. */
  value?: string;
};

const AUTO_OPEN_PARAMS: Partial<Record<PopupName, AutoOpenParam>> = {
  SubscriptionPopup: { param: SearchParam.AUTO_OPEN_PAYWALL },
  TrialMonthlyRebillPopup: {
    param: SearchParam.AUTO_OPEN_TRIAL_MONTHLY_REBILL,
  },
  AnnualPlanPopup: {
    param: SearchParam.MODAL,
    value: MODAL_SEARCH_PARAM_VALUE.UPGRADE_ANNUAL,
  },
  RescuePopup: {
    param: SearchParam.MODAL,
    value: MODAL_SEARCH_PARAM_VALUE.RESCUE,
  },
  PayAsYouGoPopup: { param: SearchParam.OPEN_PAY_AS_YOU_GO_POPUP },
};

/**
 * The link works only where the host mounts this island; elsewhere it stays
 * in the URL, and no login is asked for.
 */
const AUTO_OPEN_ISLANDS: Partial<Record<PopupName, IslandName>> = {
  RescuePopup: 'RescuePopup',
};

/** A guest logs in first; closing the login keeps the link in the URL. */
const AUTO_OPEN_LOGIN_REQUIRED: Partial<Record<PopupName, boolean>> = {
  RescuePopup: true,
  PayAsYouGoPopup: true,
};

type AutoOpenData<K extends PopupName> = {
  /** The params the payload is read from; they go away with the link. */
  params?: SearchParam[];
  getData: (searchParams: URLSearchParams) => PopupDataMap[K];
};

const AUTO_OPEN_DATA: { [K in PopupName]?: AutoOpenData<K> } = {
  RescuePopup: {
    getData: () => ({ source: RESCUE_POPUP_SOURCE.DEEP_LINK }),
  },
  // `&variant=offer` of a marketing link; the popup checks it.
  PayAsYouGoPopup: {
    params: [SearchParam.VARIANT],
    getData: (searchParams) => ({
      variant: searchParams.get(SearchParam.VARIANT) ?? undefined,
    }),
  },
};

/**
 * Who a link opens the popup for, by the fresh userData. A popup with a
 * condition is opened through the login, which brings that userData.
 */
const AUTO_OPEN_CONDITIONS: Partial<
  Record<PopupName, (userData: UserDataModel) => boolean>
> = {
  RescuePopup: ({ isUserSubscribed }) => !isUserSubscribed,
};

type AutoOpenLink = {
  name: PopupName;
  data?: PopupDataMap[PopupName];
  params: SearchParam[];
  isLoginRequired: boolean;
};

const getAutoOpenLinks = (
  names: PopupName[],
  searchParams: URLSearchParams
): AutoOpenLink[] =>
  names.flatMap((name) => {
    const autoOpenParam = AUTO_OPEN_PARAMS[name];

    if (!autoOpenParam || !searchParams.has(autoOpenParam.param)) {
      return [];
    }

    const { param, value } = autoOpenParam;

    if (value !== undefined && searchParams.get(param) !== value) {
      return [];
    }

    const island = AUTO_OPEN_ISLANDS[name];

    if (island && !isIslandMounted(island)) {
      return [];
    }

    const autoOpenData = AUTO_OPEN_DATA[name];

    return [
      {
        name,
        data: autoOpenData?.getData(searchParams),
        params: [param, ...(autoOpenData?.params ?? [])],
        isLoginRequired:
          Boolean(AUTO_OPEN_LOGIN_REQUIRED[name]) ||
          Boolean(AUTO_OPEN_CONDITIONS[name]),
      },
    ];
  });

type UsePopupAutoOpenOptions = {
  /** Popups this root renders. */
  names: PopupName[];
};

/**
 * Opens on mount the popups whose link is on the host URL — after the login
 * when the popup needs one, and only for the users its condition lets in —
 * then removes the link: it has done its job, and a reload does not open the
 * popup again.
 */
export const usePopupAutoOpen = ({ names }: UsePopupAutoOpenOptions) => {
  const { openPopup } = usePopups();
  const [searchParams] = useSearchParams();
  const deleteSearchParams = useDeleteSearchParams();
  const { runLoggedIn } = useLoginThen({
    flow: LoginEventPopupFlow.PRICING,
    source: 'usePopupAutoOpen',
  });
  // The names and the URL of the first render: links are handled once per page
  // load, so a caller that builds a new array on every render cannot reopen a
  // popup the user has just closed.
  const initialRef = useRef({ names, searchParams });
  const isHandledRef = useRef(false);
  // A link behind a login is removed once the login is through, against the
  // URL of that moment, not the one of the first render.
  const deleteSearchParamsRef = useRef(deleteSearchParams);

  useEffect(() => {
    deleteSearchParamsRef.current = deleteSearchParams;
  }, [deleteSearchParams]);

  useEffect(() => {
    if (isHandledRef.current) {
      return;
    }

    isHandledRef.current = true;

    const links = getAutoOpenLinks(
      initialRef.current.names,
      initialRef.current.searchParams
    );
    const loginLinks = links.filter(({ isLoginRequired }) => isLoginRequired);
    // Removed in one go: two removals in a row would each start from the same
    // URL, and the second would bring back what the first removed.
    const handledParams: SearchParam[] = [];
    let isHandlingNow = true;

    links
      .filter(({ isLoginRequired }) => !isLoginRequired)
      .forEach(({ name, data, params }) => {
        openPopup(name, data);
        handledParams.push(...params);
      });

    if (loginLinks.length > 0) {
      runLoggedIn((userData) => {
        loginLinks.forEach(({ name, data }) => {
          if (AUTO_OPEN_CONDITIONS[name]?.(userData) ?? true) {
            openPopup(name, data);
          }
        });

        const loginParams = loginLinks.flatMap(({ params }) => params);

        // A logged-in user gets here at once, together with the other links.
        if (isHandlingNow) {
          handledParams.push(...loginParams);
        } else {
          deleteSearchParamsRef.current(loginParams);
        }
      });
    }

    isHandlingNow = false;

    if (handledParams.length > 0) {
      deleteSearchParams(handledParams);
    }
  }, [deleteSearchParams, openPopup, runLoggedIn]);
};
